import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import ChartAttribution from '../../components/ChartAttribution';
import { getDataColor, CHART_AXIS_COLOR, CHART_GRID_COLOR } from '../../utils/chartColors';
import { formatObservationPeriod } from '../../utils/formatObservationPeriod';
import {
  applyChartTransform,
  applyRangePreset,
  axisPeriodLabel,
  axisValueLabel,
  CHART_SERIES_SOFT_LIMIT,
  hasMixedMarks,
  hasPlottableChartValues,
  normalizeChartSpec,
  resolveSeriesMark,
  seriesToRechartsData,
  sortSeriesForPaint,
  trimLeadingNullChartRows,
  yAxisDomain,
} from './chartSpec';
import { ChartLegend, ChartToolbar } from './ChartToolbar';
import { ChartTooltip } from './ChartTooltip';
import './OikraChart.css';

function buildColorsById(series, customColors) {
  const map = {};
  series.forEach((item, index) => {
    map[item.id] = item.color || getDataColor(index, customColors);
  });
  return map;
}

/**
 * OikraChart — shared interactive chart shell (Recharts renderer).
 *
 * @param {object} props
 * @param {import('./chartSpec').ChartSpec} props.spec
 * @param {string[]} [props.customColors]
 * @param {(format: 'png'|'svg', ctx: object) => void} [props.onExport]
 * @param {(spec: import('./chartSpec').ChartSpec) => void} [props.onSpecChange]
 * @param {number} [props.height]
 * @param {string} [props.className]
 * @param {string} [props.locale]
 */
export function OikraChart({
  spec: inputSpec,
  customColors = [],
  onExport,
  onSpecChange,
  height,
  className = '',
  locale = 'es-AR',
}) {
  const normalized = useMemo(() => normalizeChartSpec(inputSpec), [inputSpec]);
  const { spec, warnings, suggestSmallMultiples, dualAxisWarning } = normalized;

  const [hiddenIds, setHiddenIds] = useState(() => new Set());
  const [range, setRange] = useState(spec.range || 'all');
  const [transform, setTransform] = useState(spec.transform || 'none');
  const [fullscreen, setFullscreen] = useState(false);

  const seriesFingerprint = useMemo(
    () =>
      (spec.series || [])
        .map((s) => {
          const pts = s.points || [];
          return `${s.id}:${pts.length}:${pts[0]?.t || ''}:${pts[pts.length - 1]?.t || ''}`;
        })
        .join('|'),
    [spec.series],
  );

  // Reset toolbar when a new series payload arrives (guided rebuild / history).
  useEffect(() => {
    setRange(spec.range || 'all');
    setTransform(spec.transform || 'none');
    setHiddenIds(new Set());
  }, [seriesFingerprint, spec.range, spec.transform]);

  const series = useMemo(
    () =>
      (spec.series || []).map((item) => ({
        ...item,
        hidden: hiddenIds.has(item.id) || item.hidden,
      })),
    [spec.series, hiddenIds],
  );

  const axisFormatter = useMemo(() => (value) => axisValueLabel(value, locale, spec.decimals), [spec.decimals, locale]);

  const colorsById = useMemo(
    () => buildColorsById(spec.series || [], customColors),
    [spec.series, customColors],
  );

  const seriesById = useMemo(() => {
    const map = {};
    series.forEach((item) => {
      map[item.id] = { ...item, color: colorsById[item.id] };
    });
    return map;
  }, [series, colorsById]);

  const visibleSeries = useMemo(() => series.filter((s) => !s.hidden), [series]);

  const plotData = useMemo(() => {
    const joined = seriesToRechartsData(visibleSeries);
    // Transform on the full series first so YoY lag can use lookback outside the
    // selected range; then crop the display window.
    const transformed = applyChartTransform(joined, visibleSeries, transform);
    const ranged = applyRangePreset(transformed, range);
    return trimLeadingNullChartRows(ranged, visibleSeries);
  }, [visibleSeries, range, transform]);

  const canPlot = hasPlottableChartValues(plotData, visibleSeries);
  const emptyHint =
    (transform === 'yoy' || transform === 'mom') && visibleSeries.length
      ? transform === 'yoy'
        ? 'YoY necesita al menos 13 observaciones en nivel. Elegí Interanual en el asistente o ampliá el período.'
        : 'MoM necesita al menos 2 observaciones en nivel.'
      : 'Sin datos para graficar';

  const density = spec.density || 'analysis';
  const showToolbar = density === 'analysis';
  const chartMark = spec.mark || 'line';
  const mixedMarks = hasMixedMarks(visibleSeries, chartMark);
  const plotHeight = height || (density === 'analysis' ? 300 : 248);

  const emitSpecPatch = useCallback(
    (patch) => {
      onSpecChange?.({ ...spec, ...patch, series: spec.series });
    },
    [onSpecChange, spec],
  );

  const handleRange = (next) => {
    setRange(next);
    emitSpecPatch({ range: next });
  };

  const handleTransform = (next) => {
    setTransform(next);
    emitSpecPatch({ transform: next });
  };

  const toggleSeries = (id) => {
    setHiddenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleExport = (format) => {
    onExport?.(format, { spec, data: plotData, series: visibleSeries, range, transform });
  };

  const periodLabel = formatObservationPeriod(
    spec.provenance?.observationPeriod,
    locale.startsWith('en') ? 'en' : 'es',
  );
  const ChartImpl = mixedMarks
    ? ComposedChart
    : chartMark === 'bar'
      ? BarChart
      : chartMark === 'area'
        ? AreaChart
        : LineChart;
  const useDualAxis = Boolean(spec.dualAxis) && visibleSeries.some((s) => s.axis === 1);
  const paintSeries = mixedMarks ? sortSeriesForPaint(visibleSeries, chartMark) : visibleSeries;

  const marks = paintSeries.map((item) => {
    const color = colorsById[item.id];
    const yAxisId = useDualAxis && item.axis === 1 ? 'right' : 'left';
    const mark = resolveSeriesMark(item, chartMark);
    if (mark === 'bar') {
      return (
        <Bar
          key={item.id}
          dataKey={item.id}
          name={item.label}
          fill={color}
          yAxisId={yAxisId}
          stackId={item.stack ? 'stack' : undefined}
          radius={[3, 3, 0, 0]}
          maxBarSize={28}
        />
      );
    }
    if (mark === 'area') {
      return (
        <Area
          key={item.id}
          type="monotone"
          dataKey={item.id}
          name={item.label}
          stroke={color}
          fill={color}
          fillOpacity={item.stack ? 0.55 : 0.12}
          strokeWidth={2}
          dot={false}
          yAxisId={yAxisId}
          stackId={item.stack ? 'stack' : undefined}
          connectNulls
        />
      );
    }
    return (
      <Line
        key={item.id}
        type="monotone"
        dataKey={item.id}
        name={item.label}
        stroke={color}
        strokeWidth={2}
        dot={false}
        activeDot={{ r: 4 }}
        yAxisId={yAxisId}
        connectNulls
      />
    );
  });

  return (
    <figure
      className={[
        'oikra-chart',
        `oikra-chart--${density}`,
        fullscreen ? 'is-fullscreen' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <header className="oikra-chart__header">
        {spec.title ? <h3 className="oikra-chart__title">{spec.title}</h3> : null}
        {spec.subtitle ? <p className="oikra-chart__subtitle">{spec.subtitle}</p> : null}
      </header>

      {(dualAxisWarning
        || suggestSmallMultiples
        || warnings.includes('incompatible_units_prefer_index_or_small_multiples')
        || warnings.includes('composition_requires_additive_same_unit')
        || warnings.includes('composition_parts_should_sum_to_total')) && (
        <div className="oikra-chart__warnings">
          {dualAxisWarning ? (
            <p className="oikra-chart__warning oikra-chart__warning--emphasis">
              Eje dual activo: como máximo dos unidades, rotuladas en cada eje.
            </p>
          ) : null}
          {suggestSmallMultiples ? (
            <p className="oikra-chart__warning">
              Más de {CHART_SERIES_SOFT_LIMIT} series visibles: considerá small multiples para legibilidad.
            </p>
          ) : null}
          {warnings.includes('incompatible_units_prefer_index_or_small_multiples') && !dualAxisWarning ? (
            <p className="oikra-chart__warning">
              Unidades incompatibles: indexá a 100 o separá en paneles.
            </p>
          ) : null}
          {warnings.includes('composition_requires_additive_same_unit') ? (
            <p className="oikra-chart__warning">
              Descomposición: las partes deben compartir unidad aditiva; usá base 100 o paneles.
            </p>
          ) : null}
          {warnings.includes('composition_parts_should_sum_to_total') ? (
            <p className="oikra-chart__warning">
              Descomposición: verificá que las partes apiladas sumen el total.
            </p>
          ) : null}
        </div>
      )}

      {showToolbar ? (
        <ChartToolbar
          range={range}
          transform={transform}
          onRangeChange={handleRange}
          onTransformChange={handleTransform}
          onExport={handleExport}
          fullscreen={fullscreen}
          onToggleFullscreen={() => setFullscreen((value) => !value)}
        />
      ) : null}

      <ChartLegend series={series} colorsById={colorsById} onToggle={toggleSeries} />

      <div className="oikra-chart__plot" style={{ height: plotHeight }}>
        {!plotData.length || !visibleSeries.length || !canPlot ? (
          <div className="oikra-chart__empty">{emptyHint}</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ChartImpl data={plotData} margin={{ top: 8, right: useDualAxis ? 16 : 12, left: 4, bottom: 4 }}>
              <CartesianGrid stroke={CHART_GRID_COLOR} vertical={false} />
              <XAxis
                dataKey="t"
                stroke={CHART_AXIS_COLOR}
                tick={{ fontSize: 11 }}
                tickFormatter={(t) => axisPeriodLabel(t, locale)}
                minTickGap={24}
              />
              <YAxis
                yAxisId="left"
                domain={yAxisDomain(visibleSeries.map((item) => (useDualAxis ? item : { ...item, axis: 0 })), chartMark, 0)}
                stroke={CHART_AXIS_COLOR}
                tick={{ fontSize: 11 }}
                width={48}
                tickFormatter={axisFormatter}
                label={
                  spec.yAxisLabel
                    ? { value: spec.yAxisLabel, angle: -90, position: 'insideLeft', style: { fill: CHART_AXIS_COLOR, fontSize: 11 } }
                    : undefined
                }
              />
              {useDualAxis ? (
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  domain={yAxisDomain(visibleSeries, chartMark, 1)}
                  stroke={CHART_AXIS_COLOR}
                  tick={{ fontSize: 11 }}
                  width={48}
                  tickFormatter={axisFormatter}
                  label={
                    spec.yAxisLabelSecondary
                      ? {
                          value: spec.yAxisLabelSecondary,
                          angle: 90,
                          position: 'insideRight',
                          style: { fill: CHART_AXIS_COLOR, fontSize: 11 },
                        }
                      : undefined
                  }
                />
              ) : null}
              <Tooltip
                content={(props) => (
                  <ChartTooltip
                    {...props}
                    seriesById={seriesById}
                    provenance={spec.provenance}
                    locale={locale}
                    decimals={spec.decimals}
                  />
                )}
              />
              {/* Keep Recharts Legend off; we use accessible ChartLegend above. */}
              <Legend content={() => null} />
              {marks}
            </ChartImpl>
          </ResponsiveContainer>
        )}
      </div>

      <footer className="oikra-chart__footer">
        <div className="oikra-chart__meta">
          {spec.provenance?.coverageLabel && (
            <span>
              <strong>Cobertura</strong>
              {' · '}
              {spec.provenance.coverageLabel}
            </span>
          )}
          {periodLabel && periodLabel !== spec.provenance?.coverageLabel && (
            <span>
              <strong>Período</strong>
              {' · '}
              {periodLabel}
            </span>
          )}
          <ChartAttribution source={spec.provenance?.source} />
        </div>
      </footer>
    </figure>
  );
}

export default OikraChart;
